import type { SkillConfig } from "@/generated";
import {
  SKILL_MD,
  buildSkillMd,
  parseSkillMd,
  skillFromParsed,
} from "./skillMd";

const isIgnored = (relativePath: string): boolean =>
  relativePath
    .split("/")
    .some((segment) => segment.startsWith(".") || segment === "__MACOSX");

// A skill package is a handful of small markdown files. This bound exists so a
// crafted archive cannot inflate until the tab runs out of memory. JSZip does
// not expose the uncompressed size on its public API, so an entry that does not
// report one counts as zero — the bound is a guard against the obvious case,
// not a promise.
const MAX_TOTAL_UNCOMPRESSED_BYTES = 25 * 1024 * 1024;

export async function extractSkillsFromZip(
  file: File,
  maxTotalBytes: number = MAX_TOTAL_UNCOMPRESSED_BYTES
): Promise<SkillConfig[]> {
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(file);

  const entries = Object.entries(zip.files).filter(
    ([path, entry]) => !entry.dir && !isIgnored(path)
  );

  // Longest matching prefix wins, so a file under a nested skill folder belongs
  // to that skill rather than its parent.
  const prefixes = entries
    .filter(([path]) => path.split("/").pop() === SKILL_MD)
    .map(([path]) => path.slice(0, -SKILL_MD.length))
    .sort((a, b) => b.length - a.length);

  if (prefixes.length === 0) {
    throw new Error(`No ${SKILL_MD} found in the ZIP file.`);
  }

  // Measured on the decompressed text rather than jszip's `_data`: that field
  // is internal, and reading it through an `?? 0` fallback meant a dependency
  // bump could make every entry count as zero and retire the bound in silence.
  // Sequential so an oversized archive stops at the entry that crosses the
  // line instead of after decompressing all of it.
  const files: { path: string; content: string }[] = [];
  let totalBytes = 0;
  for (const [path, entry] of entries) {
    let content: string;
    try {
      content = await entry.async("string");
    } catch {
      // Binary resources have no text representation.
      continue;
    }
    totalBytes += content.length;
    if (totalBytes > maxTotalBytes) {
      throw new Error("That ZIP is too large to import.");
    }
    files.push({ path, content });
  }

  const byPrefix = new Map<string, Record<string, string>>(
    prefixes.map((prefix) => [prefix, {}])
  );
  const skillMdByPrefix = new Map<string, string>();

  for (const { path, content } of files) {
    const prefix = prefixes.find((p) => path.startsWith(p));
    if (prefix === undefined) continue;

    const relativeName = path.slice(prefix.length);
    if (relativeName === SKILL_MD) {
      skillMdByPrefix.set(prefix, content);
    } else if (relativeName) {
      byPrefix.get(prefix)![relativeName] = content;
    }
  }

  return prefixes.map((prefix) =>
    skillFromParsed(
      parseSkillMd(skillMdByPrefix.get(prefix) ?? ""),
      byPrefix.get(prefix)!,
      prefix.split("/").filter(Boolean).pop() || ""
    )
  );
}

export const skillFolderName = (name: string): string =>
  name
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "skill";

export async function buildSkillZip(skill: SkillConfig): Promise<Blob> {
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  const folder = skillFolderName(skill.name);

  zip.file(`${folder}/${SKILL_MD}`, buildSkillMd(skill));
  for (const [name, content] of Object.entries(skill.resources ?? {})) {
    zip.file(`${folder}/${name}`, content);
  }

  return zip.generateAsync({ type: "blob" });
}
